import { Api, apiRequest } from '@/api'

const CACHE_UNTIL = 5 * 60 * 1000

let common // saves common variables (entities, dictionaries and transforms)
const columnKeysByEntityKey = {} // saves schema data (columnKeys and relations) for each entity
// hardcoded functions available at backend
const FUNCTIONS = [
    'COUNT',
    'SORT',
    'UNIQUE',
    'MIN',
    'MAX',
    'SUM',
    'NOW',
    'DIFF_TIME',
    'DIFF_MONTHS',
    'MEAN',
    'MEAN_LIST',
    'FROM_DATE',
    'SPLIT',
    'RESOLVE',
    'moment',
]
// reserverd words and some other ones that we consider they could be used within a default expression
const ALLOWED_JS_WORDS = [
    'boolean',
    'break',
    'byte',
    'case',
    'catch',
    'char',
    'class',
    'continue',
    'const',
    'default',
    'delete',
    'do',
    'double',
    'else',
    'enum',
    'export',
    'extends',
    'false',
    'final',
    'finally',
    'float',
    'for',
    'function',
    'if',
    'in',
    'instanceof',
    'int',
    'let',
    'long',
    'native',
    'new',
    'null',
    'private',
    'protected',
    'public',
    'return',
    'short',
    'static',
    'super',
    'switch',
    'synchronized',
    'this',
    'throw',
    'throws',
    'true',
    'try',
    'typeof',
    'var',
    'void',
    'while',
    'with',
    'Array',
    'Boolean',
    'Date',
    'Infinity',
    'Math',
    'NaN',
    'Number',
    'Object',
    'parseFloat',
    'parseInt',
    'RegExp',
    'String',
    'toString',
    'valueOf',
]

// returns a promise with common variables. They are cached temporally.
function fetchCommonVariableList () {
    if (Date.now() < (common?.updatedAt || 0) + CACHE_UNTIL) {
        return Promise.resolve(common.data)
    }

    return Promise.all([
        apiRequest(Api().entities.list()),
        apiRequest(Api().dictionaries.list()),
        apiRequest(Api().entities.transforms()),
    ])
        .then(([{ data: entities }, { data: dictionaries }, { data: transforms }]) => {
            common = {
                data: {
                    // list of all entity keys
                    entityKeys: entities.map(e => e.key),
                    // list of all dictionary keys
                    dictionaryKeys: dictionaries.map(e => e.key),
                    // list of all transform keys
                    transformKeys: transforms.map(e => e.key),
                },
                updatedAt: Date.now(),
            }
            return common.data
        })
        .catch(() => {
            common = null
            throw new Error()
        })
}

// returns a promise with columnKeys and relatedEntityKeys for a specific entitity. They are cached temporally.
function fetchColumnKeysAndRelatedEntities (entityKey) {
    if (Date.now() < (columnKeysByEntityKey[entityKey]?.updatedAt || 0) + CACHE_UNTIL) {
        return Promise.resolve(columnKeysByEntityKey[entityKey].data)
    }
    return apiRequest(Api().entities.schema({ entityKey }))
        .then(({ data: { fields, relations } }) => {
            columnKeysByEntityKey[entityKey] = {
                data: {
                    // list of all column keys for this entity
                    columnKeys: fields.map(f => f.key),
                    // list of all related entity to this one. We store its related name and entityKey
                    relationList: Object.keys(relations).map(name => ({ name, entityKey: relations[name].entity })),
                },
                updatedAt: Date.now(),
            }
            return columnKeysByEntityKey[entityKey].data
        })
        .catch(() => {
            columnKeysByEntityKey[entityKey] = null
            throw new Error()
        })
}

// returns a promise with columnKeys and relations (entities and their columnKeys) for a specific entitity. They are cached temporally.
function fetchEntityColumnKeysAndRelations (entityKey) {
    return fetchColumnKeysAndRelatedEntities(entityKey)
        .then(({ columnKeys, relationList }) => Promise.all(relationList.map(relation => fetchColumnKeysAndRelatedEntities(relation.entityKey)))
            .then(entitiesList => ({
                // list of all column keys for this entity
                columnKeys,
                // object containing all the relations (related entities). Each one with its entityKey and column keys
                relations: entitiesList.reduce((acc, e, i) => ({
                    ...acc,
                    [relationList[i].name]: {
                        entityKey: relationList[i].entityKey,
                        columnKeys: e.columnKeys,
                    },
                }), {}),
            })))
}

// returns a promise with all the variable information. They are chached temporally.
export default function fetchPredefVariables (entityKey) {
    return Promise.all([
        fetchCommonVariableList(),
        fetchEntityColumnKeysAndRelations(entityKey),
    ])
        .then(([{ entityKeys, dictionaryKeys, transformKeys }, { columnKeys, relations }]) => ({
            entityKeys,
            dictionaryKeys,
            transformKeys,
            functions: FUNCTIONS,
            allowedJsWords: ALLOWED_JS_WORDS,
            columnKeys,
            relations,
        }))
}
